import React, { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { socialService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useCartsQuery } from '../hooks/useCartsQuery'
import SharedCartPreview from './SharedCartPreview'
import SharedCartAttachment from './SharedCartAttachment'
import ConversationCartPicker from './ConversationCartPicker'
import LiquidGlassButton from './LiquidGlassButton'
import Lottie from './Lottie'
import { 
  initSupabaseRealtime, 
  subscribeToMessages, 
  unsubscribeFromMessages,
  isRealtimeAvailable 
} from '../services/realtime'
import './MessagesPanel.css'

function MessagesPanel({ selectedConversation, onSelectConversation, onUserClick }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [messageContent, setMessageContent] = useState('')
  const [showFriendList, setShowFriendList] = useState(false)
  const [selectedCartId, setSelectedCartId] = useState(null)
  const [showCartSelector, setShowCartSelector] = useState(false)
  const [showCartPreview, setShowCartPreview] = useState(false)
  const [previewCartId, setPreviewCartId] = useState(null)
  const [previewCartName, setPreviewCartName] = useState(null)
  const [cartAnimation, setCartAnimation] = useState(null)
  const cartLottieRef = useRef(null)
  const cartButtonRef = useRef(null)
  const sendingRef = useRef(false)
  const composerRef = useRef(null)
  const [sendPulse, setSendPulse] = useState(false)
  const messagesEndRef = useRef(null)
  const messagesListRef = useRef(null)
  const stickToBottomRef = useRef(true)
  const userJustSentRef = useRef(false)
  const prevFirstMessageIdRef = useRef(null)
  const prevScrollHeightRef = useRef(0)
  const [animatingMessageIds, setAnimatingMessageIds] = useState(new Set())
  const lastMessageCountRef = useRef(0)
  const [pendingMessages, setPendingMessages] = useState([])
  const [olderMessages, setOlderMessages] = useState([])
  const [hasMoreOlder, setHasMoreOlder] = useState(true)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const lastSelectedConversationRef = useRef(null)
  const previewScrollRef = useRef(0)
  const loadingOlderRef = useRef(false)

  const isNearBottom = (node) => {
    if (!node) return true
    return node.scrollHeight - node.scrollTop - node.clientHeight <= 96
  }

  const scrollListToEnd = (behavior = 'auto') => {
    const node = messagesListRef.current
    if (!node) return
    const reduce = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (behavior === 'smooth' && !reduce && typeof node.scrollTo === 'function') {
      node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' })
      return
    }
    node.scrollTop = node.scrollHeight
  }

  // Récupérer les conversations
  const { data: conversationsData, error: conversationsError } = useQuery({
    queryKey: ['social-conversations'],
    queryFn: () => socialService.getConversations(),
    retry: false,
    refetchInterval: (query) => {
      if (query.state.error?.response?.status === 405) {
        return false
      }
      return 30000
    }
  })

  // Récupérer la liste d'amis pour démarrer une nouvelle conversation
  const { data: friendsData, error: friendsError, isLoading: isLoadingFriends } = useQuery({
    queryKey: ['social-friends'],
    queryFn: () => socialService.getFriends(),
    retry: false,
    enabled: showFriendList
  })

  // Récupérer les messages de la conversation sélectionnée
  const { data: messagesData, error: messagesError, isLoading: isLoadingMessages, refetch: refetchMessages } = useQuery({
    queryKey: ['social-messages', selectedConversation],
    queryFn: () => {
      const isRealtime = isRealtimeAvailable()
      if (isRealtime) {
        console.log('%c[Polling] ⏰ Polling de secours (Realtime actif, polling toutes les 10s)', 'color: #ffaa00; font-style: italic;')
      } else {
        console.log('%c[Polling] 🔄 Polling actif (Realtime non disponible, polling toutes les 2s)', 'color: #ffaa00; font-weight: bold;')
      }
      return socialService.getMessages(selectedConversation)
    },
    enabled: !!selectedConversation,
    retry: false,
    // Utiliser un intervalle plus long si Realtime est disponible, sinon polling toutes les 2 secondes
    refetchInterval: (query) => {
      if (query.state.error?.response?.status === 405) {
        return false
      }
      // Si Realtime est disponible, on peut augmenter l'intervalle car les messages arrivent en temps réel
      return isRealtimeAvailable() ? 10000 : 2000
    }
  })

  const conversations = conversationsData?.conversations || []
  const latestMessages = messagesData?.messages || []
  const messages = React.useMemo(() => {
    const seen = new Set()
    const merged = []
    for (const message of [...olderMessages, ...latestMessages]) {
      if (!message?.id || seen.has(message.id)) continue
      seen.add(message.id)
      merged.push(message)
    }
    return merged
  }, [olderMessages, latestMessages])
  const friends = friendsData?.friends || []
  const currentConversation = conversations.find(c => c.user.id === selectedConversation)
  
  // Combiner les messages réels avec les messages en attente
  const allMessages = React.useMemo(() => {
    if (!selectedConversation) return messages
    // Filtrer les messages en attente pour cette conversation
    const pendingForConversation = pendingMessages.filter(m => m.receiver_id === selectedConversation)
    // Créer un Set des IDs de messages réels pour éviter les doublons
    const realMessageIds = new Set(messages.map(m => m.id))
    // Filtrer les messages en attente qui ne sont pas encore dans les messages réels
    // Garder les messages temporaires (même ceux mis à jour avec les données réelles) jusqu'à ce que le message réel arrive
    const uniquePending = pendingForConversation.filter(m => {
      // Si le message a un tempId, c'est un message temporaire mis à jour, le garder si le message réel n'existe pas encore
      if (m.tempId) {
        return !realMessageIds.has(m.id)
      }
      // Pour les messages temporaires purs (commençant par "temp-"), les garder
      if (m.id.startsWith('temp-')) {
        return true
      }
      // Pour les autres messages, vérifier s'ils existent dans les messages réels
      return !realMessageIds.has(m.id)
    })
    // Combiner et trier par date
    const combined = [...messages, ...uniquePending]
    return combined.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
  }, [messages, pendingMessages, selectedConversation])

  // Récupérer les paniers de l'utilisateur
  const { data: cartsData } = useCartsQuery()
  const carts = cartsData ?? []

  // Charger l'animation Cadis
  useEffect(() => {
    fetch('/Cadis_animation.json')
      .then(res => res.json())
      .then(data => setCartAnimation(data))
      .catch(err => console.error('Erreur chargement animation Cadis:', err))
  }, [])

  
  // Debug: vérifier les données de la conversation
  useEffect(() => {
    if (currentConversation) {
      console.log('[MessagesPanel] currentConversation:', currentConversation)
      console.log('[MessagesPanel] user data:', currentConversation.user)
    }
  }, [currentConversation])
  
  // Filtrer les amis pour exclure ceux qui ont déjà une conversation
  const friendsWithoutConversation = friends.filter(friend => 
    !conversations.some(conv => conv.user.id === friend.id)
  )

  // Nettoyer les messages temporaires quand le message réel arrive
  useEffect(() => {
    if (messages.length === 0) return
    
    const realMessageIds = new Set(messages.map(m => m.id))
    
    setPendingMessages(prev => {
      // Si aucun message temporaire, ne rien faire
      if (prev.length === 0) return prev
      
      // Filtrer les messages temporaires qui ont maintenant leur équivalent réel
      const filtered = prev.filter(m => {
        // Si le message a un tempId, c'est un message temporaire mis à jour
        // Le retirer seulement si le message réel existe maintenant
        if (m.tempId) {
          return !realMessageIds.has(m.id)
        }
        // Pour les messages temporaires purs (commençant par "temp-"), les garder
        if (m.id.startsWith('temp-')) {
          return true
        }
        // Pour les autres messages, vérifier s'ils existent dans les messages réels
        return !realMessageIds.has(m.id)
      })
      
      // Ne mettre à jour que si quelque chose a changé (évite les boucles infinies)
      if (filtered.length === prev.length) {
        return prev
      }
      
      return filtered
    })
  }, [messages]) // Retirer pendingMessages des dépendances pour éviter la boucle

  // Défilement : coller au bas seulement si la lecture y est déjà, ou après un envoi.
  // Un prepend d'historique conserve le point de lecture. scrollIntoView est évité
  // car il fait défiler les ancêtres (page / social) plutôt que la liste.
  const prevMessagesLengthRef = useRef(0)
  useEffect(() => {
    const list = messagesListRef.current
    const prevLength = prevMessagesLengthRef.current
    const prevFirstId = prevFirstMessageIdRef.current
    const firstId = allMessages[0]?.id ?? null
    const grew = allMessages.length > prevLength
    const prepended = grew && firstId && prevFirstId && firstId !== prevFirstId

    if (prepended && list && !stickToBottomRef.current) {
      const previousHeight = prevScrollHeightRef.current || list.scrollHeight
      requestAnimationFrame(() => {
        const node = messagesListRef.current
        if (!node) return
        node.scrollTop += node.scrollHeight - previousHeight
      })
    } else if (userJustSentRef.current) {
      userJustSentRef.current = false
      stickToBottomRef.current = true
      requestAnimationFrame(() => scrollListToEnd('smooth'))
    } else if (grew && (stickToBottomRef.current || prevLength === 0)) {
      requestAnimationFrame(() => scrollListToEnd(prevLength === 0 ? 'auto' : 'smooth'))
    }

    if (grew && prevLength > 0) {
      const newMessages = allMessages.slice(prevLength)
      newMessages.forEach(message => {
        if (message.sender_id === user?.id && !message.isPending) {
          setAnimatingMessageIds(prev => new Set([...prev, message.id]))
          setTimeout(() => {
            setAnimatingMessageIds(prev => {
              const newSet = new Set(prev)
              newSet.delete(message.id)
              return newSet
            })
          }, 500)
        }
      })
    }

    prevMessagesLengthRef.current = allMessages.length
    prevFirstMessageIdRef.current = firstId
    if (list) prevScrollHeightRef.current = list.scrollHeight
  }, [allMessages, user?.id])

  useEffect(() => {
    const syncConversationLock = () => {
      const narrow = typeof window !== 'undefined'
        && window.matchMedia('(max-width: 768px)').matches
      document.documentElement.classList.toggle(
        'conversation-open',
        Boolean(selectedConversation) && narrow
      )
    }
    syncConversationLock()
    const viewport = typeof window !== 'undefined' ? window.visualViewport : null
    const syncKeyboard = () => {
      if (!viewport) return
      const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      document.documentElement.style.setProperty('--keyboard-inset', `${Math.round(inset)}px`)
    }
    syncKeyboard()
    window.addEventListener('resize', syncConversationLock)
    viewport?.addEventListener('resize', syncKeyboard)
    viewport?.addEventListener('scroll', syncKeyboard)
    return () => {
      window.removeEventListener('resize', syncConversationLock)
      viewport?.removeEventListener('resize', syncKeyboard)
      viewport?.removeEventListener('scroll', syncKeyboard)
      document.documentElement.classList.remove('conversation-open')
      document.documentElement.style.removeProperty('--keyboard-inset')
    }
  }, [selectedConversation])

  // Initialiser Supabase Realtime une seule fois au montage du composant
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    // Récupérer les credentials Supabase depuis les variables d'environnement
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
    
    if (supabaseUrl && supabaseAnonKey && token) {
      try {
        initSupabaseRealtime(supabaseUrl, supabaseAnonKey, token)
        console.log('[MessagesPanel] Supabase Realtime initialisé')
      } catch (error) {
        console.warn('[MessagesPanel] Realtime non disponible, utilisation du polling:', error)
      }
    } else {
      console.log('[MessagesPanel] Realtime non configuré - utilisation du polling uniquement')
    }
    
    // Nettoyer à la déconnexion
    return () => {
      // Ne pas nettoyer le client Supabase ici, seulement les abonnements
      // Le client sera réutilisé si le composant se remonte
    }
  }, []) // Exécuter une seule fois au montage

  // S'abonner aux messages en temps réel pour la conversation sélectionnée
  useEffect(() => {
    if (!selectedConversation || !user?.id || !isRealtimeAvailable()) {
      return
    }

    const unsubscribe = subscribeToMessages(
      user.id,
      selectedConversation,
      (newMessage, eventType = 'insert') => {
        // Quand un nouveau message arrive via Realtime
        if (eventType === 'insert') {
          console.log('%c[MessagesPanel] ✨ Message Realtime traité - Mise à jour de l\'UI immédiatement', 'color: #00ff00; font-weight: bold;', {
            message_id: newMessage.id,
            content: newMessage.content?.substring(0, 50) + '...'
          })
          
          // Ajouter le nouveau message à la liste
          queryClient.setQueryData(['social-messages', selectedConversation], (oldData) => {
            if (!oldData) return oldData
            
            // Vérifier si le message n'existe pas déjà
            const messageExists = oldData.messages?.some(m => m.id === newMessage.id)
            if (messageExists) {
              console.log('[MessagesPanel] ⚠️ Message déjà présent, ignoré')
              return oldData
            }
            
            console.log('%c[MessagesPanel] ✅ Nouveau message ajouté via Realtime (pas de polling nécessaire)', 'color: #00ff00; font-weight: bold;')
            return {
              ...oldData,
              messages: [...(oldData.messages || []), newMessage]
            }
          })
          
        } else if (eventType === 'update') {
          // Mettre à jour le statut de lecture
          queryClient.setQueryData(['social-messages', selectedConversation], (oldData) => {
            if (!oldData) return oldData
            
            return {
              ...oldData,
              messages: oldData.messages?.map(m => 
                m.id === newMessage.id ? { ...m, read_at: newMessage.read_at } : m
              ) || []
            }
          })
        }
        
        // Invalider les conversations pour mettre à jour le badge de non lus
        queryClient.invalidateQueries(['social-conversations'])
      }
    )

    return () => {
      unsubscribe()
    }
  }, [selectedConversation, user?.id, queryClient])

  // Marquer comme lu quand on ouvre une conversation
  useEffect(() => {
    if (selectedConversation) {
      socialService.markAsRead(selectedConversation)
      queryClient.invalidateQueries(['social-conversations'])
    }
  }, [selectedConversation])

  useEffect(() => {
    setOlderMessages([])
    setHasMoreOlder(true)
    setLoadingOlder(false)
    loadingOlderRef.current = false
  }, [selectedConversation])

  useEffect(() => {
    if (typeof messagesData?.has_more === 'boolean' && olderMessages.length === 0) {
      setHasMoreOlder(messagesData.has_more)
    }
  }, [messagesData?.has_more, olderMessages.length])

  const loadOlderMessages = async () => {
    if (!selectedConversation || loadingOlderRef.current || !hasMoreOlder) return
    const first = allMessages[0]
    if (!first?.created_at) return
    const list = messagesListRef.current
    prevScrollHeightRef.current = list?.scrollHeight || 0
    loadingOlderRef.current = true
    setLoadingOlder(true)
    try {
      const data = await socialService.getMessages(selectedConversation, 1, 50, first.created_at)
      const rows = Array.isArray(data?.messages) ? data.messages : []
      setHasMoreOlder(Boolean(data?.has_more) && rows.length > 0)
      if (rows.length) {
        setOlderMessages((current) => {
          const seen = new Set(current.map((item) => item.id))
          return [...rows.filter((item) => item?.id && !seen.has(item.id)), ...current]
        })
      }
    } catch {
      setHasMoreOlder(false)
    } finally {
      loadingOlderRef.current = false
      setLoadingOlder(false)
    }
  }

  // En ouvrant une conversation, se placer sur les messages récents.
  useEffect(() => {
    const conversationChanged = lastSelectedConversationRef.current !== selectedConversation
    if (selectedConversation && conversationChanged) {
      lastSelectedConversationRef.current = selectedConversation
      stickToBottomRef.current = true
      prevMessagesLengthRef.current = 0
      prevFirstMessageIdRef.current = null
      if (!isLoadingMessages && allMessages.length > 0) {
        requestAnimationFrame(() => scrollListToEnd('auto'))
      }
      return
    }
    if (selectedConversation) {
      lastSelectedConversationRef.current = selectedConversation
    }
  }, [selectedConversation, isLoadingMessages, allMessages.length])

  // Mutation pour envoyer un message
  const sendMessageMutation = useMutation({
    mutationFn: ({ content, sharedCartId }) => socialService.sendMessage(selectedConversation, content, sharedCartId)
  })

  const resizeComposer = (node) => {
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${Math.min(node.scrollHeight, 120)}px`
  }

  const handleComposerChange = (e) => {
    setMessageContent(e.target.value)
    resizeComposer(e.target)
  }

  const handleComposerKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent?.isComposing) return
    e.preventDefault()
    handleSendMessage(e)
  }

  const handleSendMessage = (e) => {
    e.preventDefault()
    const sending = sendMessageMutation.isPending || sendMessageMutation.isLoading
    if (sendingRef.current || sending) return
    if ((!messageContent.trim() && !selectedCartId) || !selectedConversation) return
    
    // Sauvegarder le contenu avant de vider le champ
    const contentToSend = messageContent.trim() || ' '
    const cartIdToSend = selectedCartId
    sendingRef.current = true
    
    // Créer un message temporaire pour l'affichage optimiste
    const tempId = `temp-${Date.now()}-${Math.random()}`
    const selectedCart = cartIdToSend ? carts.find(c => c.id === cartIdToSend) : null
    const tempMessage = {
      id: tempId,
      sender_id: user?.id,
      receiver_id: selectedConversation,
      content: contentToSend,
      shared_cart_id: cartIdToSend,
      shared_cart: selectedCart ? {
        id: selectedCart.id,
        name: selectedCart.name,
        items_count: selectedCart.items_count || 0,
        total: selectedCart.total || 0,
        preview_items: selectedCart.preview_items || [],
      } : null,
      created_at: new Date().toISOString(),
      read_at: null,
      isPending: true
    }
    
    // Ajouter le message temporaire à la liste
    setPendingMessages(prev => [...prev, tempMessage])
    setAnimatingMessageIds(prev => new Set([...prev, tempId]))
    
    setMessageContent('')
    setSelectedCartId(null)
    if (composerRef.current) {
      composerRef.current.style.height = 'auto'
    }
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!reduceMotion) {
      setSendPulse(true)
      window.setTimeout(() => setSendPulse(false), 420)
    }
    
    userJustSentRef.current = true
    stickToBottomRef.current = true
    requestAnimationFrame(() => scrollListToEnd('smooth'))
    
    // Envoyer le message
    sendMessageMutation.mutate({ 
      content: contentToSend, 
      sharedCartId: cartIdToSend 
    }, {
      onSuccess: (response) => {
        if (response?.id) {
          // Mettre à jour le message temporaire avec les données réelles
          setPendingMessages(prev => prev.map(m => 
            m.id === tempId ? { ...response, isPending: false, tempId: tempId } : m
          ))
          
          // Ajouter l'animation pour le message réel
          setAnimatingMessageIds(prev => {
            const newSet = new Set(prev)
            newSet.delete(tempId)
            newSet.add(response.id)
            return newSet
          })
          
          // Retirer l'animation après qu'elle soit terminée
          setTimeout(() => {
            setAnimatingMessageIds(prev => {
              const newSet = new Set(prev)
              newSet.delete(response.id)
              return newSet
            })
          }, 500)
          
          // Mettre à jour la liste des conversations
          queryClient.invalidateQueries(['social-conversations'])
          
          // Si Realtime est disponible, le message arrivera automatiquement via Realtime
          // Sinon, on refetch mais avec gestion d'erreur silencieuse
          if (!isRealtimeAvailable()) {
            refetchMessages().catch(err => {
              if (err?.response?.status !== 401) {
                console.error('[MessagesPanel] Erreur lors du refetch des messages:', err)
              }
            })
          }
        }
        sendingRef.current = false
      },
      onError: () => {
        sendingRef.current = false
        setPendingMessages(prev => prev.filter(m => m.id !== tempId))
        setAnimatingMessageIds(prev => {
          const newSet = new Set(prev)
          newSet.delete(tempId)
          return newSet
        })
        setMessageContent((prev) => prev || (contentToSend === ' ' ? '' : contentToSend))
        if (cartIdToSend) {
          setSelectedCartId((prev) => prev || cartIdToSend)
        }
        requestAnimationFrame(() => resizeComposer(composerRef.current))
      }
    })
  }

  const handleShareCart = (cart) => {
    setSelectedCartId(cart.id)
    setShowCartSelector(false)
  }

  const handleViewCart = (cartId, cartName) => {
    previewScrollRef.current = messagesListRef.current?.scrollTop || 0
    setPreviewCartId(cartId)
    setPreviewCartName(cartName)
    setShowCartPreview(true)
  }

  const handleCloseCartPreview = () => {
    setShowCartPreview(false)
    requestAnimationFrame(() => {
      const node = messagesListRef.current
      if (!node) return
      node.scrollTop = previewScrollRef.current
    })
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)

    if (minutes < 1) return 'À l\'instant'
    if (minutes < 60) return `Il y a ${minutes} min`
    if (hours < 24) return `Il y a ${hours}h`
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
  }

  const handleStartConversation = (friendId) => {
    setShowFriendList(false)
    if (onSelectConversation) {
      onSelectConversation(friendId)
    }
  }

  // Debug: vérifier l'état
  useEffect(() => {
    if (showFriendList) {
      console.log('[MessagesPanel] showFriendList:', showFriendList)
      console.log('[MessagesPanel] friendsData:', friendsData)
      console.log('[MessagesPanel] friends:', friends)
      console.log('[MessagesPanel] friendsWithoutConversation:', friendsWithoutConversation)
      console.log('[MessagesPanel] friendsError:', friendsError)
    }
  }, [showFriendList, friendsData, friends, friendsWithoutConversation, friendsError])

  return (
    <div className={`messages-panel${selectedConversation ? ' has-selection' : ''}`}>
      <div className="conversations-list">
        <div className="conversations-header">
          <h3>Conversations</h3>
          <div
            className="plusButton new-conversation-btn"
            onClick={() => setShowFriendList(!showFriendList)}
            title="Nouvelle conversation"
            tabIndex={0}
          >
            <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
              <defs>
                <mask id="mask0_21_345_messages">
                  <rect width="30" height="30" fill="white"/>
                </mask>
              </defs>
              <g mask="url(#mask0_21_345_messages)">
                <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
              </g>
            </svg>
          </div>
        </div>
        
        {showFriendList && (
          <div className="friends-list-for-message">
            <h4>Démarrer une conversation</h4>
            {isLoadingFriends ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9em', padding: '10px', fontStyle: 'italic' }}>
                Chargement...
              </p>
            ) : friendsError?.response?.status === 405 ? (
              <p style={{ color: '#ff6b6b', fontSize: '0.9em', padding: '10px' }}>
                ⚠️ Endpoint non disponible
              </p>
            ) : friendsWithoutConversation.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9em', padding: '10px', fontStyle: 'italic' }}>
                {friends.length === 0 
                  ? 'Vous n\'avez pas encore d\'amis' 
                  : 'Tous vos amis ont déjà une conversation'}
              </p>
            ) : (
              friendsWithoutConversation.map(friend => (
                <div
                  key={friend.id}
                  className="friend-item-for-message"
                  onClick={() => handleStartConversation(friend.id)}
                >
                  <div className="user-avatar">
                    {friend.avatar_url ? (
                      <img src={friend.avatar_url} alt="" />
                    ) : (
                      (friend.display_name?.charAt(0) || 
                       friend.username?.charAt(0) || 
                       friend.email?.charAt(0) || 
                       'U').toUpperCase()
                    )}
                  </div>
                  <span className="friend-name">
                    {friend.display_name || 
                     (friend.username ? `@${friend.username}` : null) || 
                     friend.email || 
                     'Utilisateur'}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {conversationsError?.response?.status === 405 ? (
          <p className="no-conversations" style={{ color: '#ff6b6b' }}>
            ⚠️ Endpoint non disponible
          </p>
        ) : conversations.length === 0 ? (
          <p className="no-conversations">Aucune conversation</p>
        ) : (
          conversations.map(conv => (
            <div
              key={conv.user.id}
              className={`conversation-item ${selectedConversation === conv.user.id ? 'active' : ''}`}
              onClick={() => onSelectConversation && onSelectConversation(conv.user.id)}
            >
              <div className="user-avatar">
                {conv.user.avatar_url ? (
                  <img src={conv.user.avatar_url} alt="" />
                ) : (
                  (conv.user.display_name?.charAt(0) || 
                   conv.user.username?.charAt(0) || 
                   conv.user.email?.charAt(0) || 
                   'U').toUpperCase()
                )}
              </div>
              <div className="conversation-info">
                <div className="conversation-header">
                  <div className="user-name-wrapper">
                    {conv.user.display_name && (
                      <span className="user-name">{conv.user.display_name}</span>
                    )}
                    {conv.user.username && (
                      <span className="user-username">@{conv.user.username}</span>
                    )}
                    {!conv.user.display_name && !conv.user.username && (
                      <span className="user-name">{conv.user.email || 'Utilisateur'}</span>
                    )}
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="unread-badge">{conv.unread_count}</span>
                  )}
                </div>
                <p className="last-message">{conv.last_message?.content || 'Aucun message'}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="messages-view">
        {selectedConversation ? (
          <>
            <div className="messages-header">
              <button
                type="button"
                className="messages-back-btn"
                onClick={() => onSelectConversation(null)}
                aria-label="Retour aux conversations"
                title="Retour aux conversations"
              >
                <img src="/Icon arrow back ios new.png" alt="" />
              </button>
              <div 
                className="messages-header-user clickable"
                onClick={() => {
                  if (onUserClick && currentConversation?.user.id) {
                    onUserClick(currentConversation.user.id)
                  }
                }}
                title="Voir le profil"
              >
                <div className="messages-header-avatar">
                  {currentConversation?.user.avatar_url ? (
                    <img src={currentConversation.user.avatar_url} alt="" />
                  ) : (
                    <span className="avatar-initial">
                      {(currentConversation?.user.display_name?.charAt(0) || 
                        currentConversation?.user.username?.charAt(0) || 
                        currentConversation?.user.email?.charAt(0) || 
                        'U').toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="messages-header-info">
                  {currentConversation?.user.display_name ? (
                    <>
                      <h3 className="messages-header-name">{currentConversation.user.display_name}</h3>
                      {currentConversation.user.username && (
                        <span className="messages-header-username">@{currentConversation.user.username}</span>
                      )}
                    </>
                  ) : currentConversation?.user.username ? (
                    <h3 className="messages-header-name">@{currentConversation.user.username}</h3>
                  ) : (
                    <h3 className="messages-header-name">{currentConversation?.user.email || 'Utilisateur'}</h3>
                  )}
                </div>
              </div>
            </div>
            <div
              className="messages-list"
              ref={messagesListRef}
              onScroll={() => {
                const node = messagesListRef.current
                if (!node) return
                stickToBottomRef.current = isNearBottom(node)
                prevScrollHeightRef.current = node.scrollHeight
                if (node.scrollTop < 48) {
                  void loadOlderMessages()
                }
              }}
            >
              {hasMoreOlder && allMessages.length > 0 ? (
                <button
                  type="button"
                  className="messages-load-older"
                  onClick={() => { void loadOlderMessages() }}
                  disabled={loadingOlder}
                >
                  {loadingOlder ? 'Chargement…' : 'Messages précédents'}
                </button>
              ) : null}
              {isLoadingMessages ? (
                <div className="messages-loading">
                  <div className="loader"></div>
                  <p className="loading-text">Chargement des messages...</p>
                </div>
              ) : messagesError?.response?.status === 405 ? (
                <p className="no-messages" style={{ color: '#ff6b6b' }}>
                  ⚠️ Endpoint non disponible
                </p>
              ) : allMessages.length === 0 ? (
                <p className="no-messages">Aucun message. Commencez la conversation !</p>
              ) : (
                allMessages.map(message => {
                  const isOwn = message.sender_id === user?.id
                  const isAnimating = isOwn && animatingMessageIds.has(message.id)
                  const isPending = message.isPending
                  const isRead = message.read_at !== null && message.read_at !== undefined
                  
                  return (
                    <div key={message.id} className={`message-item ${isOwn ? 'own' : ''} ${isAnimating ? 'message-sending' : ''} ${isPending ? 'message-pending' : ''}`}>
                      <div className="message-stack">
                        {message.content && message.content.trim() && (
                          <div className="message-content">
                            <p>{message.content}</p>
                          </div>
                        )}
                        {(message.shared_cart || message.shared_cart_id) && (
                          <SharedCartAttachment
                            cart={message.shared_cart}
                            sharedCartId={message.shared_cart_id}
                            own={isOwn}
                            variant="message"
                            playKey={`msg:${message.id}`}
                            onOpen={handleViewCart}
                          />
                        )}
                        <div className="message-footer">
                          <span className="message-time">{formatDate(message.created_at)}</span>
                          {isOwn && (
                            <span className={`message-status ${isPending ? 'status-pending' : isRead ? 'status-read' : 'status-sent'}`}>
                              {isPending ? (
                                <span className="status-icon">⏳</span>
                              ) : isRead ? (
                                <span className="status-icon">✓✓</span>
                              ) : (
                                <span className="status-icon">✓</span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </div>
            <form onSubmit={handleSendMessage} className="message-form">
              {selectedCartId && (
                <div className="selected-cart-preview">
                  <span>Panier sélectionné: {carts.find(c => c.id === selectedCartId)?.name || 'Panier'}</span>
                  <button 
                    type="button" 
                    className="remove-cart-btn"
                    onClick={() => setSelectedCartId(null)}
                  >
                    ✕
                  </button>
                </div>
              )}
              <div className="message-composer-row">
                <textarea
                  ref={composerRef}
                  className="message-input"
                  rows={1}
                  placeholder={selectedCartId ? "Ajouter un message (optionnel)..." : "Écrire un message..."}
                  value={messageContent}
                  onChange={handleComposerChange}
                  onKeyDown={handleComposerKeyDown}
                  enterKeyHint="send"
                  autoComplete="off"
                  aria-label="Rédiger un message"
                />
                <span ref={cartButtonRef} className="composer-icon-wrap">
                <LiquidGlassButton
                  type="button"
                  className="cart-selector-btn composer-icon-btn"
                  onClick={() => {
                    if (cartLottieRef.current) {
                      cartLottieRef.current.goToAndPlay(0)
                    }
                    setShowCartSelector((open) => !open)
                  }}
                  title="Partager un panier"
                  aria-label="Partager un panier"
                  aria-expanded={showCartSelector}
                  aria-haspopup="listbox"
                >
                  {cartAnimation ? (
                    <Lottie
                      lottieRef={cartLottieRef}
                      animationData={cartAnimation}
                      loop={false}
                      autoplay={false}
                      style={{ width: 22, height: 22 }}
                    />
                  ) : (
                    <span aria-hidden="true">🛒</span>
                  )}
                </LiquidGlassButton>
                </span>
                <LiquidGlassButton
                  type="submit"
                  className={`send-btn composer-icon-btn${sendPulse ? ' is-sending' : ''}${(sendMessageMutation.isPending || sendMessageMutation.isLoading) ? ' is-busy' : ''}`}
                  disabled={(!messageContent.trim() && !selectedCartId) || sendMessageMutation.isPending || sendMessageMutation.isLoading}
                  aria-label="Envoyer le message"
                  title="Envoyer le message"
                  aria-busy={sendMessageMutation.isPending || sendMessageMutation.isLoading}
                >
                  <span className="send-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="20" height="20" focusable="false">
                      <path
                        d="M4.2 11.4 20.6 4.2l-6.8 16.2-2.6-6.2-6.2-2.8z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M11.2 14.2 20.6 4.2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </LiquidGlassButton>
              </div>
            </form>
            <ConversationCartPicker
              isOpen={showCartSelector}
              anchor={cartButtonRef.current}
              carts={carts}
              onClose={() => setShowCartSelector(false)}
              onSelect={handleShareCart}
            />
            {previewCartId ? (
              <SharedCartPreview
                isOpen={showCartPreview}
                cartId={previewCartId}
                cartName={previewCartName}
                onClose={handleCloseCartPreview}
              />
            ) : null}
          </>
        ) : (
          <div className="no-conversation-selected">
            <p>Sélectionnez une conversation pour commencer</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default MessagesPanel

